import React, { ReactNode } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';

interface EmptyStateProps {
    icon?: LucideIcon;
    emoji?: string;
    title: string;
    description?: string;
    action?: {
        label: string;
        onClick: () => void;
        icon?: LucideIcon;
    };
    secondaryAction?: {
        label: string;
        onClick: () => void;
    };
    className?: string;
    children?: ReactNode;
}

const EmptyState: React.FC<EmptyStateProps> = ({
    icon: Icon,
    emoji,
    title,
    description,
    action,
    secondaryAction,
    className,
    children,
}) => {
    return (
        <Card className={cn('border-none shadow-lg bg-gray-50/50', className)}>
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
                {/* Icon or Emoji */}
                {Icon && (
                    <div className="rounded-full bg-primary/10 p-4 mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                    </div>
                )}
                {emoji && !Icon && (
                    <div className="text-6xl mb-4" role="img" aria-label="Empty state illustration">
                        {emoji}
                    </div>
                )}

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {title}
                </h3>

                {/* Description */}
                {description && (
                    <p className="text-sm text-muted-foreground mb-6 max-w-md">
                        {description}
                    </p>
                )}

                {/* Custom Children */}
                {children}

                {/* Actions */}
                {(action || secondaryAction) && (
                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        {action && (
                            <Button onClick={action.onClick} className="gap-2">
                                {action.icon && <action.icon className="h-4 w-4" />}
                                {action.label}
                            </Button>
                        )}
                        {secondaryAction && (
                            <Button onClick={secondaryAction.onClick} variant="outline">
                                {secondaryAction.label}
                            </Button>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default EmptyState;
